import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient,
} from '@angular/common/http';
import { BehaviorSubject, Observable, from, throwError, firstValueFrom } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TokenService } from './services/passbook-services/token.service';
import { environment } from 'src/environments/environment';
import { StorageKeys } from './shared-module-files/simpo.constant';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private tokenService: TokenService,
    private injector: Injector,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isLocalAIRequest = request.url.includes('127.0.0.1') || request.url.includes('localhost');
    const authReq = this.addAuthHeader(request, isLocalAIRequest);

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !isLocalAIRequest &&
          this.isSimpoApi(request.url) &&
          !this.isAuthEndpoint(request.url) &&
          !this.isPublicEndpoint(request.url) &&
          // Don't auto-refresh while the idle popup is up — the user must click Continue.
          localStorage.getItem('sessionExpired') !== 'true'
        ) {
          return this.handle401(authReq, next);
        }
        return throwError(() => error);
      }),
    );
  }

  /** Add the Bearer token for Simpo API calls unless one is already present. */
  private addAuthHeader(request: HttpRequest<unknown>, isLocalAIRequest: boolean): HttpRequest<unknown> {
    if (
      isLocalAIRequest ||
      request.headers.has('Authorization') ||
      !this.isSimpoApi(request.url) ||
      this.isNoBearerEndpoint(request.url) ||
      this.isPublicEndpoint(request.url)
    ) {
      return request;
    }
    const token = localStorage.getItem(StorageKeys.token) || this.tokenService.getToken();
    return token ? this.attachToken(request, token) : request;
  }

  /**
   * Endpoints that must NOT receive the access-token bearer.
   * staff/auth/refresh especially — the access token is usually expired when we
   * call it, so an attached bearer would get the request rejected at the gateway
   * before the refresh token in the body is even read.
   */
  private isNoBearerEndpoint(url: string): boolean {
    return (
      url.includes('staff/auth/refresh') ||
      url.includes('staff/staff/login')
    );
  }

  /**
   * Public reference/master data needed during onboarding — BEFORE the user has a
   * business-scoped token (e.g. new user with no business, "New Google User" flow).
   *  - never attach a (possibly stale) bearer the gateway would 401 on
   *  - never treat a 401 here as session-expiry -> no refresh, no logout, no loop
   */
  private isPublicEndpoint(url: string): boolean {
    return (
      url.includes('master/business/industry') ||
      url.includes('master/business/name') ||
      url.includes('master/location/country') ||
      url.includes('master/location/state') ||
      url.includes('master/location/city')
    );
  }

  /** Only our own backend should receive the Simpo bearer token. */
  private isSimpoApi(url: string): boolean {
    if (!/^https?:\/\//i.test(url)) return true; // relative URL -> our app/backend
    return url.includes('simpo.ai');
  }

  private isAuthEndpoint(url: string): boolean {
    return (
      url.includes('auth/refresh') ||
      url.includes('auth/switch-business') ||
      url.includes('staff/staff/login')
    );
  }

  /** On a 401, refresh the access token once and replay the failed request(s). */
  private handle401(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // A refresh is already in flight in THIS tab — wait for the new token, then retry.
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => next.handle(this.attachToken(request, token as string))),
      );
    }

    if (!localStorage.getItem(StorageKeys.REFRESH_TOKEN)) {
      this.logoutToLogin();
      return throwError(() => new Error('Session expired'));
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return from(this.refreshAccessToken()).pipe(
      switchMap((newToken) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(newToken);
        return next.handle(this.attachToken(request, newToken));
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        // Only a definitive 401 means the refresh token is dead -> logout.
        // Uncertain network failures are NOT retried and do NOT clear the session.
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.logoutToLogin();
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Perform the refresh, serialised across browser tabs via the Web Locks API so
   * a rotated refresh token is never reused (which would revoke the login family).
   */
  private async refreshAccessToken(): Promise<string> {
    const run = async (): Promise<string> => {
      // Another tab may have just refreshed — reuse its token instead of rotating again.
      const freshlyRefreshed = Number(localStorage.getItem('tokenRefreshedAt') || 0);
      if (Date.now() - freshlyRefreshed < 15000 && localStorage.getItem(StorageKeys.token)) {
        return localStorage.getItem(StorageKeys.token) as string;
      }

      const http = this.injector.get(HttpClient);
      const refreshToken = localStorage.getItem(StorageKeys.REFRESH_TOKEN);
      const res: any = await firstValueFrom(
        http.post(environment.baseUrl + 'staff/auth/refresh', { refreshToken }),
      );

      const data = res?.data ?? res;
      const accessToken: string = data?.accessToken || data?.token;
      if (!accessToken) {
        throw new Error('Refresh response did not contain an access token');
      }

      localStorage.setItem(StorageKeys.token, accessToken);
      localStorage.setItem('tokenRefreshedAt', String(Date.now()));
      if (data?.expiresIn != null) {
        localStorage.setItem(
          StorageKeys.ACCESS_TOKEN_EXPIRES_AT,
          String(Date.now() + Number(data.expiresIn) * 1000),
        );
      }
      if (sessionStorage.getItem('setToken') != null) {
        sessionStorage.setItem('setToken', accessToken);
      }
      if (data?.refreshToken) {
        localStorage.setItem(StorageKeys.REFRESH_TOKEN, data.refreshToken);
      }
      return accessToken;
    };

    const locks: any = (navigator as any)?.locks;
    if (locks?.request) {
      return locks.request('simpo-token-refresh', run);
    }
    return run();
  }

  private attachToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private logoutToLogin(): void {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}
    try {
      this.injector.get(Router).navigate(['']);
    } catch (e) {
      window.location.href = '/';
    }
  }
}
